import type {FC} from 'react';
import {
  Divider,
  Icon,
  IconNames,
  Paper,
  Stack,
  Typography,
} from '@wellstone-solutions/web';

import {ActionButton} from './ActionButton';
import {Modules, Theme} from '@wellstone-solutions/common';
import {GroupEventLocation} from 'modules/calendar/components/GroupEvent/GroupEventLocation';
import {getGroupEventReminderLabel} from 'modules/calendar/utils';
import type {Group} from 'api/v2';
import type {UICalendarEventType} from '@wellstone-solutions/common';
import {toV2EventUI} from '../../utils';
import {PageHeader} from 'components/PageHeader';
export const Header: FC<{
  group: Group;
  groupEvents: Array<UICalendarEventType>;
  groupMemberListRefetch: () => void;
}> = ({group, groupEvents, groupMemberListRefetch}) => {
  const {name, description} = group ?? {};

  const formEvents = groupEvents.map((event) => toV2EventUI(event.event));

  return (
    <Stack>
      <PageHeader
        title={name}
        description={description}
        hideDescriptionOnMobile={false}
        backButtonProps={{
          label: 'Back to Groups',
          path: '/groups',
        }}
        ActionButton={() => (
          <ActionButton
            group={group}
            groupMemberListRefetch={groupMemberListRefetch}
          />
        )}
      />

      {/* If you run into issues with React Hook re-renders and this component, it's because
      of the hook below. It could be refactored into a function.  */}
      {formEvents.map((event, index) => {
        // useRecurrenceDisplayText requires the v1 schema format where 0 and empty strings are expected for "null"
        const recurrenceV1Factory = {
          bymonth: 0,
          bymonthday: 0,
          bysetpos: 0,
          byweekday: '',
          byweekno: 0,
          byyearday: 0,
        };
        const v1RecurrenceFormat = {
          ...recurrenceV1Factory,
          ...event.recurrence,
        };
        const {displayTime} = Modules.Schedule.useRecurrenceDisplayText({
          calendarEvent: {...event, recurrence: v1RecurrenceFormat},
          hideRecurrence: false,
        });

        const [displayFrequency, displayTimeRange] = displayTime
          ? displayTime.split(' from ')
          : ['', ''];

        const hasInvitation = event.invitations && event.invitations.length > 0;

        const reminderLabel = hasInvitation
          ? getGroupEventReminderLabel(event.invitations[0])
          : '';

        return (
          <Paper sx={{mt: 2, p: 3}} key={`group-event-${index}`}>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              justifyContent="space-between">
              <Stack
                direction={{
                  xs: 'column',
                  md: 'row',
                }}
                sx={{
                  width: {
                    xs: '100%',
                    md: '45%',
                  },
                  mb: {
                    xs: 2,
                    md: 0,
                  },
                }}
                spacing={2}>
                <Stack sx={{flex: 1}}>
                  <Typography sx={{fontSize: 18, fontWeight: 'bold'}}>
                    Meeting Details
                  </Typography>
                  <Typography sx={{mt: 1}}>{event.title}</Typography>
                </Stack>

                <Stack direction="row" spacing={1} sx={{flex: 1}}>
                  <Icon
                    name={IconNames.Calendar}
                    color={Theme.colorPalette.dark}
                    size={28}
                    style={{flexShrink: 0}}
                  />
                  <Stack>
                    <Typography sx={{fontWeight: 'bold'}}>
                      {displayTimeRange}
                    </Typography>
                    <Typography>{displayFrequency}</Typography>
                    <Typography>{reminderLabel}</Typography>
                  </Stack>
                </Stack>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    display: {
                      xs: 'none',
                      md: 'block',
                    },
                  }}
                />
              </Stack>
              <GroupEventLocation calendarEvent={event} />
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};
